import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Play } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveSrc = (src) => {
  if (!src) return src;
  if (src.startsWith("/api/")) return `${BACKEND}${src}`;
  return src;
};

/**
 * Compact live preview for a channel tile.
 * - Shows the static logo by default.
 * - On hover (or when `alwaysOn` is true), mounts a muted, looping, autoplay
 *   <video> playing the live stream. Uses hls.js when needed, otherwise native.
 * - Unmounts video on unhover to release network/CPU.
 */
export default function ChannelTilePreview({ streamUrl, streamType = "hls", logoUrl, alt = "", alwaysOn = false, className = "" }) {
  const [localHover, setLocalHover] = useState(false);
  const hover = alwaysOn || localHover;
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  useEffect(() => {
    if (!hover || !streamUrl || !videoRef.current) return;
    const v = videoRef.current;
    const src = resolveSrc(streamUrl);
    const isHls = streamType === "hls" || src.includes(".m3u8");
    const cleanup = () => { if (hlsRef.current) { try { hlsRef.current.destroy(); } catch {} hlsRef.current = null; } };
    if (isHls && Hls.isSupported()) {
      const hls = new Hls({ maxBufferLength: 6, maxMaxBufferLength: 12, lowLatencyMode: true });
      hls.loadSource(src); hls.attachMedia(v); hlsRef.current = hls;
    } else {
      v.src = src;
    }
    v.muted = true; v.playsInline = true; v.loop = true;
    v.play().catch(() => {});
    return cleanup;
  }, [hover, streamUrl, streamType]);

  return (
    <div
      className={`relative overflow-hidden ${className}`}
      onMouseEnter={() => setLocalHover(true)}
      onMouseLeave={() => setLocalHover(false)}
      data-testid="channel-tile-preview"
    >
      {/* Base logo */}
      <img src={logoUrl} alt={alt} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${hover ? "opacity-0" : "opacity-100"}`} />
      {/* Play indicator overlay when idle */}
      {!hover && (
        <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition pointer-events-none">
          <Play className="w-4 h-4 text-white/80 drop-shadow-lg" />
        </div>
      )}
      {/* Live preview video */}
      {hover && (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          playsInline
          loop
          data-testid="channel-tile-preview-video"
        />
      )}
      {/* Faint LIVE tag on hover */}
      {hover && (
        <span className="absolute top-1 right-1 text-[8px] font-bold tracking-widest bg-[#E11D48] text-white px-1 py-0.5 rounded shadow">LIVE</span>
      )}
    </div>
  );
}
